import { Button, cn, focusRing, Sheet } from "@rhema/ui";
import { useId, useState } from "react";
import { localInputValue, shortWhen, type TimeChoice } from "./when";

/** Picks a time from a few choices or a date and time of the person's own. */
export function TimeSheet({
  open,
  title,
  subtitle,
  choices,
  pickLabel,
  onPick,
  onClose,
}: {
  open: boolean;
  title: string;
  subtitle?: string;
  choices: TimeChoice[];
  /** The button under the custom time, such as "Schedule". */
  pickLabel: string;
  onPick: (at: Date) => void;
  onClose: () => void;
}) {
  const inputId = useId();
  const [custom, setCustom] = useState(() => localInputValue(new Date(Date.now() + 60 * 60_000)));
  const [problem, setProblem] = useState<string | null>(null);
  const pickCustom = () => {
    const at = new Date(custom);
    if (Number.isNaN(at.getTime())) return setProblem("Pick a date and time.");
    if (at.getTime() < Date.now() + 60_000) return setProblem("Pick a time at least a minute from now.");
    setProblem(null);
    onPick(at);
  };
  return (
    <Sheet open={open} onClose={onClose} title={title} subtitle={subtitle} fixed={false} className="pb-7">
      <ul aria-label="Times" className="m-0 mt-4 flex list-none flex-col gap-1 p-0">
        {choices.map((c) => (
          <li key={c.id}>
            <button
              type="button"
              onClick={() => onPick(c.at)}
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-3 rounded-field px-3.5 py-3 text-left text-[15px] font-semibold hover:bg-surface-header",
                focusRing,
              )}
            >
              <span>{c.label}</span>
              <span className="text-[13px] font-normal text-ink-secondary">{shortWhen(c.at)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex flex-col gap-2 border-t border-line pt-4">
        <label htmlFor={inputId} className="text-[13px] font-semibold text-ink-secondary">
          Another time
        </label>
        <input
          id={inputId}
          type="datetime-local"
          value={custom}
          min={localInputValue(new Date())}
          onChange={(e) => setCustom(e.target.value)}
          className="h-11 rounded-field border border-line bg-surface px-3 font-sans text-[15px] text-ink focus:border-blue focus:outline-none"
        />
        {problem && (
          <p role="alert" className="m-0 text-sm text-scarlet">
            {problem}
          </p>
        )}
        <Button onClick={pickCustom}>{pickLabel}</Button>
      </div>
    </Sheet>
  );
}
